// @ts-check
import { moveActor } from './actors.js';
import { damagePlayer, playerBody } from './power.js';
import { BOTS, WORLD } from './tuning.js';
const TILE = WORLD.tile;
/** @typedef {import('./types.js').Level} Level */
/** @typedef {import('./types.js').Enemy} Enemy */
/** @param {Enemy} enemy @param {import("./types.js").EventSink} [emit] @param {boolean} [launch] */
export function defeatEnemy(enemy, emit, launch = false) {
  if (enemy.mode === 'dead') return;
  emit?.({ type: 'enemy-defeated', x: enemy.x, y: enemy.y, entityId: enemy.id });
  enemy.mode = 'dead'; enemy.launched = launch; enemy.dead = launch ? 0.6 : BOTS.deathTime;
  enemy.vy = launch ? 300 : 0; enemy.vx = launch ? -Math.sign(enemy.vx || 1) * 45 : 0;
}

/** Warden armor absorbs one stomp or pulse. Cores and Overclock still defeat it outright.
 * @param {Enemy} enemy @param {import('./types.js').EventSink} [emit] @param {boolean} [launch]
 */
export function strikeEnemy(enemy, emit, launch = false) {
  if (enemy.kind === 'warden' && (enemy.health ?? 2) > 1) {
    enemy.health = 1; enemy.attack = 'recover'; enemy.attackTime = 1.2;
    enemy.vx = 0; enemy.grace = .2;
  } else defeatEnemy(enemy, emit, launch);
}
/** Fixed tells lock an attack direction so QP can jump over or bait a recovery.
 * @param {Enemy} enemy @param {import('./player.js').Position} player @param {number} dt
 */
function advanceAttack(enemy, player, dt) {
  if (enemy.kind !== 'surge' && enemy.kind !== 'warden') return;
  const distance = player.x - enemy.x;
  if (!enemy.attack) {
    if (Math.abs(distance) > 260 || Math.abs(player.y - enemy.y) > 160 || enemy.vy !== 0) return;
    enemy.attack = 'tell'; enemy.attackTime = enemy.kind === 'surge' ? .6 : .7;
    enemy.attackDirection = Math.sign(distance) || -1; enemy.vx = 0;
  } else {
    enemy.attackTime = Math.max(0, (enemy.attackTime ?? 0) - dt);
    if (enemy.attackTime > 0) return;
    if (enemy.attack === 'tell') {
      const hopper = enemy.kind === 'warden';
      enemy.attack = hopper ? 'hop' : 'dash'; enemy.attackTime = hopper ? .6 : .55;
      enemy.vx = (enemy.attackDirection ?? -1) * (hopper ? 95 : 230);
      if (hopper) enemy.vy = 540;
    } else if (enemy.attack === 'dash' || enemy.attack === 'hop') {
      if (enemy.attack === 'hop' && enemy.vy !== 0) return;
      enemy.attack = 'recover'; enemy.attackTime = .9; enemy.vx = 0;
    } else {
      enemy.attack = undefined; enemy.vx = (enemy.attackDirection ?? -1) * BOTS.patrolSpeed;
    }
  }
}

/** Update patrols and contacts in stable authored order.
 * @param {Level} level @param {import('./player.js').Position} state @param {number} oldY @param {number} dt
 * @param {import('./types.js').EventSink} [emit]
 */
export function updateEnemies(level, state, oldY, dt, emit) {
    for (const enemy of level.enemies) {
      if (enemy.mode === 'dead') {
        enemy.dead = Math.max(0, enemy.dead - dt);
        if (enemy.launched && enemy.dead) { enemy.x += enemy.vx * dt; enemy.y += enemy.vy * dt; enemy.vy -= BOTS.gravity * dt; }
        continue;
      }
      if (enemy.x > state.x - BOTS.activationBehind && enemy.x < state.x + BOTS.activationAhead) enemy.awake = true;
      if (!enemy.awake || Math.abs(enemy.x - state.x) > BOTS.sleepDistance) continue;
      enemy.grace = Math.max(0, enemy.grace - dt);
      advanceAttack(enemy, state, dt);
      const velocity = enemy.vx;
      moveActor(enemy, level, dt);
      if (enemy.attack === 'dash' && velocity * enemy.vx < 0) { enemy.attack = 'recover'; enemy.attackTime = .9; enemy.vx = 0; }
      if (enemy.y < -TILE * 3) { enemy.mode = 'dead'; enemy.dead = 0; continue; }
      const BODY = playerBody(state);
      const overlapX = state.x + BODY.width > enemy.x + 2 && state.x < enemy.x + enemy.width - 2;
      const overlapY = state.y < enemy.y + enemy.height && state.y + BODY.height > enemy.y + 2;
      if (!overlapX || !overlapY || enemy.grace) continue;
      if (state.overclock) { defeatEnemy(enemy, emit, true); continue; }
      const stomp = state.vy < 0 && oldY >= enemy.y + enemy.height - 6;
      if (stomp) {
        emit?.({ type: 'enemy-stomped', x: enemy.x, y: enemy.y, entityId: enemy.id });
        state.y = enemy.y + enemy.height;
        state.vy = BOTS.bounce; state.grounded = false; state.jumps = 1; state.pound = 'none'; state.impact = 0;
        if (enemy.kind === 'sentinel') { enemy.mode = 'shell'; enemy.vx = 0; enemy.height = 20; enemy.grace = 0.12; }
        else strikeEnemy(enemy, emit);
      } else if (enemy.mode === 'shell') {
        enemy.mode = 'roll'; enemy.vx = state.x + BODY.width / 2 < enemy.x + enemy.width / 2 ? BOTS.coreSpeed : -BOTS.coreSpeed; enemy.grace = 0.18;
        emit?.({ type: 'core-kicked', x: enemy.x, y: enemy.y, entityId: enemy.id });
      } else {
        damagePlayer(level, state, enemy, emit);
        if (level.status === 'dead') break;
      }
    }
    for (const shell of level.enemies) if (shell.mode === 'roll') {
      for (const enemy of level.enemies) if (enemy !== shell && enemy.mode !== 'dead' && Math.abs(shell.y - enemy.y) < enemy.height && shell.x + shell.width > enemy.x && shell.x < enemy.x + enemy.width) defeatEnemy(enemy, emit, true);
    }
}
